/**
 * Portal Inventory Page
 * Stock management for shop owners
 */

import React, { useEffect, useState } from 'react';
import portalService from '../../services/portal.service';

const PortalInventory: React.FC = () => {
  const [inventory, setInventory] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAlertas, setShowAlertas] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [stockValue, setStockValue] = useState(0);
  const [operacao, setOperacao] = useState<'set' | 'add' | 'subtract'>('set');

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await portalService.getInventory(showAlertas);
      setInventory(data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [showAlertas]);

  const handleUpdateStock = async (id: string) => {
    try {
      await portalService.updateStock(id, stockValue, operacao);
      setEditingId(null);
      loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pagali-primary"></div>
      </div>
    );
  }

  const totalStock = inventory.reduce((sum, p) => sum + (Number(p.STOCK) || 0), 0);
  const semStock = inventory.filter(p => p.STOCK <= 0).length;
  const stockBaixo = inventory.filter(p => p.STOCK > 0 && p.STOCK <= 10).length;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Inventário</h1>
          <p className="text-gray-600">Controle o stock dos seus produtos</p>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox" checked={showAlertas}
            onChange={(e) => setShowAlertas(e.target.checked)}
            className="h-4 w-4 text-pagali-primary focus:ring-pagali-primary border-gray-300 rounded"
          />
          <span className="text-sm text-gray-700">Apenas alertas (stock &le; 10)</span>
        </label>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">{error}</p>
          <button onClick={() => setError(null)} className="text-sm text-red-500 underline mt-1">Fechar</button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Produtos</p>
          <p className="text-2xl font-semibold">{inventory.length}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Stock Total</p>
          <p className="text-2xl font-semibold">{totalStock}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Sem Stock</p>
          <p className="text-2xl font-semibold text-red-600">{semStock}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Stock Baixo</p>
          <p className="text-2xl font-semibold text-orange-500">{stockBaixo}</p>
        </div>
      </div>

      {/* Inventory Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Produto</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoria</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Preço</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stock</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {inventory.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    {showAlertas ? 'Nenhum produto com stock baixo.' : 'Nenhum produto no inventário.'}
                  </td>
                </tr>
              ) : (
                inventory.map((item) => (
                  <tr key={item.ID_PRODUTO} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {item.NOME}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.CATEGORIA || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.PRECO?.toLocaleString('pt-CV')} ECV
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingId === item.ID_PRODUTO ? (
                        <div className="flex items-center gap-2">
                          <select
                            value={operacao}
                            onChange={(e) => setOperacao(e.target.value as any)}
                            className="text-xs border border-gray-300 rounded px-1 py-1"
                          >
                            <option value="set">Definir</option>
                            <option value="add">Adicionar</option>
                            <option value="subtract">Remover</option>
                          </select>
                          <input
                            type="number" min="0" value={stockValue}
                            onChange={(e) => setStockValue(parseInt(e.target.value) || 0)}
                            className="w-20 text-sm border border-gray-300 rounded px-2 py-1"
                          />
                          <button onClick={() => handleUpdateStock(item.ID_PRODUTO)}
                            className="text-xs bg-pagali-primary text-white px-2 py-1 rounded">OK</button>
                          <button onClick={() => setEditingId(null)}
                            className="text-xs text-gray-500">X</button>
                        </div>
                      ) : (
                        <span className={`text-sm font-medium ${
                          item.STOCK <= 0 ? 'text-red-600' : item.STOCK <= 10 ? 'text-orange-600' : 'text-gray-900'
                        }`}>
                          {item.STOCK} {item.UNIDADE || 'un'}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        item.STOCK <= 0 ? 'bg-red-100 text-red-800' :
                        item.STOCK <= 10 ? 'bg-orange-100 text-orange-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {item.STOCK <= 0 ? 'Esgotado' : item.STOCK <= 10 ? 'Baixo' : 'OK'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      {editingId !== item.ID_PRODUTO && (
                        <button
                          onClick={() => { setEditingId(item.ID_PRODUTO); setStockValue(item.STOCK); setOperacao('set'); }}
                          className="text-pagali-primary hover:text-pagali-primary/80"
                        >
                          Ajustar Stock
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PortalInventory;
